/**
 * Typed event bus — pure module, no React dependency.
 * Listeners are managed here; the React hook wrapper lives in src/hooks/useEventBus.ts.
 */
import type { StrategyLayer } from "@/types/strategy";

export type EventMap = {
  "community:posts-changed": void;
  "community:follow-changed": { userId: number; following: boolean };
  "community:block-changed": { userId: number; blocked: boolean };
  "locale:change": void;
  "theme:change": void;
  "structure:reset-legs": string; // layer ID
  "structure:can-reset-legs": { id: string; canResetLegs: boolean };
  "structure:reset-active-leg": string; // layer ID
  "structure:can-reset-active-leg": { id: string; canReset: boolean };
  "structure:close-active-leg": string; // layer ID
  "structure:remove": string; // layer ID
  "structure:toggle": string; // layer ID — toggle enabled/disabled
  "structure:addlayer": number | undefined; // optional expiry DTE for the new strategy
  "structure:close-addlayer": void;
  "template-picker:activate-category": string;
  "template-picker:scroll-category": string;
  "template-picker:anchor": void;
  "template-picker:enter-multi-select": void;
  "template-picker:exit-multi-select": void;
  "template-picker:cycle-multi-select": void;
  "template-picker:select-all-visible": void;
  "template-picker:deselect-all": void;
  "template-picker:multi-select-changed": boolean;
  "template-picker:selected-count-changed": number;
  "livechart:toggle": void;
  "livechart:setOpen": boolean;
  // Cross-tree modal triggers (StructureTabs → StructureGenerator)
  "strategy:save-draft": void;
  "strategy:rename": void;
  "strategy:open-picker": void;
  "strategy:limit-reached": {
    layers: StrategyLayer[];
    resolution?: "remove";
  };
  "strategy:historical-overflow-disabled": { count: number };
  // Hotkey events
  "hotkey:save": void;
  "hotkey:revert": void;
  // Cancel any pending debounced auto-save. Fired before paths that
  // overwrite the persisted layers (e.g. tab-close discard) so a stale
  // pending save doesn't fire after and clobber the new state.
  "autosave:cancel": void;
  "hotkey:tab-prev": void;
  "hotkey:tab-next": void;
  "hotkey:tab-index": number;
  "hotkey:tab-close": void;
  "hotkey:tab-new": void;
  "hotkey:tab-duplicate": void;
  "hotkey:toggle-sim": void;
  "hotkey:shortcuts": void;
  "hotkey:enter": void;
  "spotinput:set": { key: string; value: number };
  "scenario:cursor-reset": void;
  "expiry:toggle-date-strip": void;
  "expiry:toggle-time-strip": void;
  "expiry:date-selection-locked-attempt": void;
  "option-chain:open": number | undefined;
  "auth:logout": void;
  "strategies:open": void;
  "notifications:open": void;
  "ai-assistant:open": void;
  "ai-assistant:prompt": { question: string };
  "quotes:update": void;
  "upgrade:open": void;
  "infraction:detected": void;
  "backtest:complete": void;
  "kol:conversion-pulse": string; // pulse message (e.g., "🎉 {name} drove a new paid signup")
};

export type EventListener<T> = (payload: T) => void;

export const listeners = new Map<keyof EventMap, Set<EventListener<never>>>();

export function emit<K extends keyof EventMap>(
  ...[event, payload]: EventMap[K] extends void ? [K] : [K, EventMap[K]]
) {
  const set = listeners.get(event);
  if (set) {
    for (const fn of set) fn(payload as never);
  }
}
